// Prompt Resolver — pick the right master prompt slot per mode, optionally substitute inline placeholders.
//
// 3 modes:
//   Mode 1: no avatar, no gender        → AI decides gender from target market
//   Mode 2: no avatar, gender selected  → user picks gender, AI generates model
//   Mode 3: avatar + gender             → user supplies avatar (wajib gender)
//
// ★ SINGLE SOURCE OF TRUTH:
// Data produk (nama, link, gender, avatar, vision, research) di-inject oleh BACKEND
// dalam section PRODUCT DATA di hujung prompt Stage 3. Resolver hanya return template
// yang PURE supaya static prefix panjang → OpenAI prompt caching efektif (up to 90%
// input discount for prefix ≥1024 tokens).
//
// Convention for admins writing master prompt:
//   - Tulis template PURE (no {{...}} inline)
//   - Backend auto-append semua data produk/avatar/gender di hujung
//   - Kalau terpaksa guna {{...}} inline (backward compat), caching akan kurang efektif

const MODE_KEYS = {
    1: 'mode1_noAvatarNoGender',
    2: 'mode2_noAvatarWithGender',
    3: 'mode3_withAvatar'
};

export function determineMode(product, avatar) {
    const hasAvatar = !!avatar && !!avatar.id;
    const hasGender = !!product?.gender;
    if (hasAvatar) return 3;
    if (hasGender) return 2;
    return 1;
}

function normaliseGender(raw) {
    if (raw === 'male') return 'lelaki';
    if (raw === 'female') return 'wanita';
    return '';
}

function buildVars(product, avatar) {
    const gender = normaliseGender(product?.gender);
    let hijab = '-';
    if (avatar && typeof avatar.hijab === 'boolean') {
        hijab = avatar.hijab ? 'ya' : 'tidak';
    }
    return {
        product_name: product?.name || '',
        product_link: product?.tiktokUrl || product?.productLink || '',
        gender,
        avatar_age: avatar?.age || '',
        avatar_hijab: hijab
    };
}

function flatReplace(template, vars) {
    let substituted = 0;
    const result = template.replace(/\{\{(\w+)\}\}/g, (_, key) => {
        substituted++;
        return vars[key] ?? '';
    });
    if (substituted > 0) {
        // Inline {{...}} di master prompt body = cache prefix berubah per produk.
        // Data produk sudah auto-inject oleh backend di section PRODUCT DATA.
        // Admin patut rujuk section tu dari master prompt, BUKAN guna {{...}} inline.
        console.warn(
            `[promptResolver] ⚠ Master prompt ada ${substituted} inline {{...}} placeholder — ` +
            `ini KURANGKAN cache effectiveness (setiap produk = cache miss). ` +
            `Cadangan: rujuk "DATA PRODUK di bawah" dalam template, biarkan backend inject data.`
        );
    }
    return result;
}

// Returns the pure master prompt template for the given mode, with any inline
// {{...}} placeholders substituted. Data produk di-inject oleh BACKEND, bukan di sini.
export function resolveMasterPrompt(module, product, avatar) {
    const mp = module?.masterPrompt;
    if (!mp) return '';

    const mode = determineMode(product, avatar);
    const slotKey = MODE_KEYS[mode];

    // Resolve the static template
    let template;
    if (typeof mp === 'string') {
        // Legacy safety net
        template = mp;
    } else {
        template = mp[slotKey] || mp[MODE_KEYS[1]] || '';
    }

    // Flat replace any inline {{...}} (backward compat — reduces cache effectiveness if used)
    return flatReplace(template, buildVars(product, avatar));
}

// Resolve placeholders inside mandatoryImageAppend / mandatoryVideoAppend.
// These texts are appended AFTER AI response and go to Veo/Gemini, NOT OpenAI —
// so zero token impact. Safe to sprinkle placeholders freely.
//
// Supported placeholders:
//   {{pronoun}}        → "She" / "He" / "She/he" (Mode 1)
//   {{pronoun_lower}}  → "she" / "he" / "she/he"
//   {{gender}}         → "lelaki" / "wanita" / "" (Mode 1)
//   {{avatar_age}}     → e.g. "26-35" (Mode 3 only)
//   {{avatar_hijab}}   → "ya" / "tidak" / "-"
export function resolveMandatoryAppend(template, product, avatar) {
    if (!template) return '';
    const genderRaw = product?.gender;
    const pronoun = genderRaw === 'female' ? 'She' : genderRaw === 'male' ? 'He' : 'She/he';
    const vars = {
        ...buildVars(product, avatar),
        pronoun,
        pronoun_lower: pronoun.toLowerCase()
    };
    return flatReplace(template, vars);
}

// Helper for UI — which placeholders are valid for a given mode
export const PLACEHOLDERS_BY_MODE = {
    1: ['product_name', 'product_link'],
    2: ['product_name', 'product_link', 'gender'],
    3: ['product_name', 'product_link', 'gender', 'avatar_age', 'avatar_hijab']
};

export const MODE_LABELS = {
    1: 'No Avatar + No Gender',
    2: 'No Avatar + Pilih Gender',
    3: 'Ada Avatar + Gender'
};

export const MODE_HINTS = {
    1: 'AI decide gender ikut target market produk',
    2: 'User pilih gender, AI generate model',
    3: 'User upload avatar sendiri (wajib pilih gender)'
};
